import { i18n as i18next } from 'i18next';
import { CampaignType } from 'modules/domains/campaign';
import {
  ConfidenceCell,
  HeaderCell,
  HoldoutDetailsCell,
  LiftCell,
  RowCell,
  UseVariantCell,
  VariantDetailsCell,
  WinningCriteriaRateCell,
} from 'modules/experiments/components/ui/variantTable/cells';
import {
  HAS_LAUNCHED_STATES,
  MetricIdentifier,
  metricsMediumSendDefaults,
  NA,
} from 'modules/experiments/constants';
import { CellAlignTypes, ExperimentColumnData, RowDataProps } from 'modules/experiments/types';
import { ExperimentState } from 'modules/graphql';
import { TFunction } from 'react-i18next';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { localizeNumber } from 'utils/l10n';
import {
  calculateRatePercentage,
  calculateSafeRatePercentage,
  italicizedTFunction,
  printUniqueMetricName,
  retrieveMetricResult,
  stylishMetricResult,
} from './common';
import {
  getExperimentStatesWithUseVariantAction,
  isExperimentHoldoutRow,
  isExperimentVariantBreakdownRow,
  isExperimentVariantSummaryRow,
} from './ruleEngine';
import { getTestingSubjectText } from './table';

type VariantBreakdownTableColumnDataArgs = {
  experimentsT: TFunction;
  metricsT: TFunction;
  i18n: i18next;
  defaultMetricColumns: string[];
  selectedMetricsColumn: string[];
  metricMap: Record<string, MetricIdentifier>;
  winningCriteriaName: string;
  state: ExperimentState;
};

type VariantSummaryTableColumnDataArgs = {
  t: TFunction;
  winningCriteriaName: string;
  state: ExperimentState;
  campaignType: CampaignType;
};

export const EmptyCell = <RowCell dataTestId="experiment-empty-cell" />;

export const TestSubjectRowCell = styled(RowCell)`
  min-width: ${spacing.x24};
`;

export const getVariantTableBaseColumnData = (t: TFunction): ExperimentColumnData[] => [
  {
    value: 'variantDetails',
    align: CellAlignTypes.flexStart,
    HeaderCell: () => <HeaderCell headerName={t('variantTable.header.variant')} />,
    RowCell: ({ row }: RowDataProps) => {
      if (isExperimentHoldoutRow(row)) return <HoldoutDetailsCell row={row} />;
      return (
        <RowCell isWinner={row.winner.toString()}>
          <VariantDetailsCell row={row} />
        </RowCell>
      );
    },
  },
  {
    value: 'testSubject',
    align: CellAlignTypes.flexStart,
    HeaderCell: () => <HeaderCell headerName={t('variantTable.header.testSubject')} />,
    RowCell: ({ row }: RowDataProps) => {
      if (isExperimentHoldoutRow(row)) return EmptyCell;
      const secondaryText = getTestingSubjectText({
        content: row.content,
        sendTime: row.sendTime,
        sendTimeUseRecipientTimeZones: row.sendTimeUseRecipientTimeZones,
        messageMedium: row.messageMedium,
        control: row.control,
        type: row.testSubject,
        t: italicizedTFunction(t),
      });

      return (
        <TestSubjectRowCell
          dataTestId="experiment-test-subject-cell"
          isWinner={row.winner.toString()}
          primaryText={t(`experimentType.${row.testSubject}`)}
          secondaryText={secondaryText}
        />
      );
    },
  },
];

export const getVariantBreakdownTableDefaultColumnData = ({
  metricsT: t,
  i18n,
  defaultMetricColumns,
  selectedMetricsColumn,
  metricMap,
  winningCriteriaName,
  state,
}: VariantBreakdownTableColumnDataArgs): ExperimentColumnData[] => {
  return defaultMetricColumns
    .filter((columnName) => selectedMetricsColumn.includes(columnName))
    .map((columnName) => ({
      value: columnName,
      align: CellAlignTypes.flexEnd,
      HeaderCell: () => {
        return (
          <HeaderCell
            headerName={i18n.exists(`metrics:${columnName}`) ? t(columnName) : columnName}
            align={CellAlignTypes.flexEnd}
          />
        );
      },
      RowCell: ({ row }: RowDataProps) => {
        if (isExperimentVariantSummaryRow(row)) return EmptyCell;

        // If, we don't receive a metric value. We simply print N/A
        const { value = NA, metricDisplayType } = retrieveMetricResult({
          dataStore: row,
          metricMap,
          metric: columnName,
        });

        if (isExperimentHoldoutRow(row)) {
          if (columnName !== winningCriteriaName) {
            return (
              <RowCell dataTestId="holdout-zero-cell" align={CellAlignTypes.flexEnd}>
                0
              </RowCell>
            );
          }

          if (value !== NA) {
            const { rate: holdoutMetricRate, percentage: holdoutMetricPercentage } =
              calculateRatePercentage({
                count: value,
                total: row?.userCount ?? 1,
              });
            const displayHoldoutSecondaryText =
              !Number.isNaN(holdoutMetricRate) && Number.isFinite(holdoutMetricRate);

            return (
              <RowCell
                dataTestId={`${columnName}-cell`}
                isWinner={'false'}
                primaryText={stylishMetricResult(value, metricDisplayType)}
                align={CellAlignTypes.flexEnd}
                {...(displayHoldoutSecondaryText ? { secondaryText: holdoutMetricPercentage } : {})}
              />
            );
          }
          return (
            <RowCell
              dataTestId={`${columnName}-cell`}
              isWinner={'false'}
              primaryText={NA}
              secondaryText={NA}
              align={CellAlignTypes.flexEnd}
            />
          );
        }

        if (isExperimentVariantBreakdownRow(row)) {
          const sendsMetricName = metricsMediumSendDefaults[row.messageMedium];
          const sends =
            retrieveMetricResult({
              dataStore: row,
              metricMap,
              metric: sendsMetricName,
            })?.value ?? 0;

          if (value !== NA) {
            const { rate: metricRate, percentage: metricPercentage } = calculateRatePercentage({
              count: value,
              total: sends,
            });
            const displaySecondaryText =
              sendsMetricName !== columnName &&
              !Number.isNaN(metricRate) &&
              Number.isFinite(metricRate);

            return (
              <RowCell
                dataTestId={`${columnName}-cell`}
                isWinner={row.winner.toString()}
                primaryText={stylishMetricResult(value, metricDisplayType)}
                align={CellAlignTypes.flexEnd}
                {...(displaySecondaryText ? { secondaryText: metricPercentage } : {})}
              />
            );
          }
          // Only set first default column to 0 if no data and
          // experiment has been launched
          if (columnName === sendsMetricName && HAS_LAUNCHED_STATES.includes(state)) {
            return (
              <RowCell
                dataTestId={`${columnName}-cell`}
                isWinner={row.winner.toString()}
                primaryText={'0'}
                align={CellAlignTypes.flexEnd}
              />
            );
          }
          return (
            <RowCell
              dataTestId={`${columnName}-cell`}
              isWinner={row.winner.toString()}
              primaryText={NA}
              secondaryText={columnName !== sendsMetricName ? NA : ''}
              align={CellAlignTypes.flexEnd}
            />
          );
        }

        return EmptyCell;
      },
    }));
};

export const getVariantBreakdownTableAdditionalColumnData = ({
  metricsT: t,
  i18n,
  selectedMetricsColumn,
  metricMap,
}: VariantBreakdownTableColumnDataArgs): ExperimentColumnData[] => {
  return selectedMetricsColumn.map((columnName) => ({
    value: columnName,
    align: CellAlignTypes.flexStart,
    HeaderCell: () => (
      <HeaderCell
        headerName={i18n.exists(`metrics:${columnName}`) ? t(columnName) : columnName}
        align={CellAlignTypes.flexEnd}
      />
    ),
    RowCell: ({ row }: RowDataProps) => {
      if (isExperimentVariantSummaryRow(row)) return EmptyCell;
      // If, we don't receive a metric value. We simply print N/A
      const { value = NA, metricDisplayType } = retrieveMetricResult({
        dataStore: row,
        metricMap,
        metric: columnName,
      });
      return (
        <RowCell
          dataTestId={`${columnName}-cell`}
          primaryText={stylishMetricResult(value, metricDisplayType)}
          align={CellAlignTypes.flexEnd}
        />
      );
    },
  }));
};

export const getVariantBreakdownTableColumnData = (
  args: VariantBreakdownTableColumnDataArgs,
): ExperimentColumnData[] => {
  return [
    ...getVariantTableBaseColumnData(args.experimentsT),
    ...getVariantBreakdownTableDefaultColumnData(args),
    // Display the conversion events first
    ...getVariantBreakdownTableAdditionalColumnData({
      ...args,
      selectedMetricsColumn: args.selectedMetricsColumn
        .filter(
          (columnName) =>
            !args.defaultMetricColumns.includes(columnName) &&
            (args.metricMap[columnName] === MetricIdentifier.CONVERSION ||
              args.metricMap[columnName] === MetricIdentifier.UNIQUE_CONVERSION),
        )
        ?.sort((a, b) => a.localeCompare(b)),
    }),
    // Followed by the rest of the system events
    ...getVariantBreakdownTableAdditionalColumnData({
      ...args,
      selectedMetricsColumn: args.selectedMetricsColumn
        .filter(
          (columnName) =>
            !args.defaultMetricColumns.includes(columnName) &&
            args.metricMap[columnName] === MetricIdentifier.SYSTEM_METRICS,
        )
        ?.sort((a, b) => a.localeCompare(b)),
    }),
  ];
};

export const getVariantSummaryTableColumnData = ({
  t,
  winningCriteriaName,
  state,
  campaignType,
}: VariantSummaryTableColumnDataArgs): ExperimentColumnData[] => {
  return [
    ...getVariantTableBaseColumnData(t),
    {
      value: 'summarySends',
      align: CellAlignTypes.flexEnd,
      HeaderCell: () => (
        <HeaderCell headerName={t('variantTable.header.sends')} align={CellAlignTypes.flexEnd} />
      ),
      RowCell: ({ row }: RowDataProps) => {
        if (isExperimentHoldoutRow(row)) return <RowCell align={CellAlignTypes.flexEnd}>0</RowCell>;
        if (!isExperimentVariantSummaryRow(row)) return EmptyCell;
        return (
          <RowCell
            dataTestId="experiment-summary-sends-cell"
            isWinner={row.winner.toString()}
            primaryText={localizeNumber(row.sends || 0)}
            align={CellAlignTypes.flexEnd}
          />
        );
      },
    },
    {
      value: 'winningCriteria',
      align: CellAlignTypes.flexEnd,
      HeaderCell: () => (
        <HeaderCell
          headerName={printUniqueMetricName(
            t('variantTable.cell.unique'),
            winningCriteriaName,
            t('variantTable.cell.rate'),
          )}
          align={CellAlignTypes.flexEnd}
        />
      ),
      RowCell: ({ row }: RowDataProps) => {
        if (isExperimentHoldoutRow(row)) {
          const { userCount, winningCriteriaCount } = row;

          return (
            <RowCell
              dataTestId="experiment-holdout-winning-criteria-cell"
              {...(userCount
                ? {
                    primaryText: calculateSafeRatePercentage({
                      count: row.winningCriteriaCount || 0,
                      total: row.userCount || 0,
                    }).percentage,
                    secondaryText: localizeNumber(winningCriteriaCount || 0),
                  }
                : {
                    primaryText: NA,
                  })}
              align={CellAlignTypes.flexEnd}
            />
          );
        }

        if (!isExperimentVariantSummaryRow(row)) return EmptyCell;
        return <WinningCriteriaRateCell row={row} />;
      },
    },
    {
      value: 'lift',
      align: CellAlignTypes.flexEnd,
      HeaderCell: () => (
        <HeaderCell
          headerName={t('variantTable.header.lift')}
          tooltipContent={t('variantTable.tooltip.lift')}
          align={CellAlignTypes.flexEnd}
        />
      ),
      RowCell: ({ row }: RowDataProps) => {
        if (!isExperimentVariantSummaryRow(row)) return EmptyCell;
        return <LiftCell row={row} />;
      },
    },
    {
      value: 'confidence',
      align: CellAlignTypes.flexEnd,
      HeaderCell: () => (
        <HeaderCell
          headerName={t('variantTable.header.confidence')}
          tooltipContent={t('variantTable.tooltip.confidence')}
          align={CellAlignTypes.flexEnd}
        />
      ),
      RowCell: ({ row }: RowDataProps) => {
        if (!isExperimentVariantSummaryRow(row)) return EmptyCell;
        return <ConfidenceCell row={row} />;
      },
    },
    ...(getExperimentStatesWithUseVariantAction(campaignType).includes(state)
      ? [
          {
            value: 'action',
            align: CellAlignTypes.flexStart,
            HeaderCell: () => <HeaderCell headerName={t('variantTable.header.action')} />,
            RowCell: ({ row }: RowDataProps) => {
              if (!isExperimentVariantSummaryRow(row)) return EmptyCell;
              return <UseVariantCell row={row} />;
            },
          },
        ]
      : []),
  ];
};

export function sortControlVariantFirst<T extends { control: boolean }>(variants: T[]) {
  return variants.reduce<T[]>(
    (acc, variant) => (variant.control ? [variant, ...acc] : [...acc, variant]),
    [],
  );
}
